import {useState, useCallback, useMemo, useEffect} from 'react'
import {useBi, useEnvironment} from '@wix/yoshi-flow-editor'
import {TabItem} from 'wix-ui-tpa/cssVars'
import {Experience as ExperienceProto} from '@wix/ambassador-table-reservations-v1-experience/types'

import {useExperiencesListStorage} from '../storage'
import {getLogger, PageType} from '../../../utils/getLogger'
import {AppPage} from '../../../utils/constants'
import {filterReservationLocationsAndExperiences} from '../../../utils/filterExperiences'

const ALL_RESERVATION_LOCATIONS_OPTION = 0

export const useHooks = ({
  allReservationLocationsTabText,
}: {
  allReservationLocationsTabText: string
}) => {
  const {isPreview, isEditor} = useEnvironment()
  const bi = useBi()
  const logger = getLogger(bi)
  const {
    breadcrumbsConfig,
    regionalSettings,
    reservationLocations: allReservationLocations,
    experiences: allExperiences,
    goToExperienceDetailsPage,
    filterConfig,
    isOrderingEnabled,
  } = useExperiencesListStorage()

  const [selectedReservationLocationTabIndex, setSelectedReservationLocationTabIndex] = useState(
    ALL_RESERVATION_LOCATIONS_OPTION,
  )

  const {reservationLocationsMap, filteredExperiences} = useMemo(
    () =>
      filterReservationLocationsAndExperiences(
        allReservationLocations,
        allExperiences,
        filterConfig,
        isEditor || isPreview,
        isOrderingEnabled,
      ),
    [allReservationLocations, allExperiences, filterConfig, isEditor, isPreview, isOrderingEnabled],
  )

  const reservationLocationTabOptions: TabItem[] = useMemo(() => {
    const allReservationLocationsOption: TabItem = {
      id: 'all',
      title: allReservationLocationsTabText,
    }

    const restReservationLocationOptions = Object.values(reservationLocationsMap).map(
      ({id, location}) => ({
        id: id!,
        title: location?.name!,
      }),
    )

    return [allReservationLocationsOption, ...restReservationLocationOptions]
  }, [allReservationLocationsTabText, reservationLocationsMap])

  const selectedTabIndex =
    selectedReservationLocationTabIndex < reservationLocationTabOptions.length
      ? selectedReservationLocationTabIndex
      : ALL_RESERVATION_LOCATIONS_OPTION

  const selectedExperiences =
    selectedTabIndex === ALL_RESERVATION_LOCATIONS_OPTION
      ? filteredExperiences
      : filteredExperiences.filter(
          ({reservationLocationId}) =>
            reservationLocationId === reservationLocationTabOptions[selectedTabIndex].id,
        )

  const showEmptyState = selectedExperiences.length === 0

  const handleReservationLocationSelect = useCallback((index: number) => {
    setSelectedReservationLocationTabIndex(index)
  }, [])

  const handleGoToExperience = useCallback(
    (experience: ExperienceProto) => {
      goToExperienceDetailsPage(experience)
    },
    [goToExperienceDetailsPage],
  )

  useEffect(() => {
    logger.isLoaded({
      isAddOn: false,
      isPreview,
      pageType: PageType.page,
      pageName: AppPage.ExperiencesList,
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    breadcrumbsConfig,
    regionalSettings,
    selectedReservationLocationTabIndex: selectedTabIndex,
    reservationLocationTabOptions,
    selectedExperiences,
    showEmptyState,
    handleReservationLocationSelect,
    handleGoToExperience,
  }
}
